import { Icon, Scrollbar } from '@aibees/shared/components';
import { classnames } from '@aibees/shared/utils';
import { Box, Stack, Typography } from '@mui/material';
import { memo, useState } from 'react';
import { useMaintainScrollForListOfAccordions } from '../../../../hooks/use-maintain-scroll-for-list-of-accordions';
import { TEMPLATE_CATEGORY_DATA_ATTRIBUTE_KEY } from '../../constant';
import { useTemplateContext } from '../../context';
import { TemplateCategory } from '../template-category';
import * as Styled from './styles';
import * as SX from './sx';
import type { TemplateCategoriesProps as Props } from './types';

export const TemplateCategories = memo(
  ({ className, checkedValues, columns, defaultExpandedCategory }: Props) => {
    const {
      items,
      createToggleOptionSelection,
      selectAllOptions,
      deselectAllOptions,
      toggleEntryChecked,
      getSelectedOptionsCount,
      handleAddEntry,
      getSelectedOptionsCountForEntry,
      handleClear,
      handleActiveEntry,
      handleRemoveEntry,
    } = useTemplateContext();

    // State to track which category is currently expanded (accordion behavior)
    const [expandedCategory, setExpandedCategory] = useState<string | null>(
      defaultExpandedCategory || null
    );

    const containerRef = useMaintainScrollForListOfAccordions(
      TEMPLATE_CATEGORY_DATA_ATTRIBUTE_KEY,
      expandedCategory ?? undefined
    );

    const handleCategoryToggle = (categoryName: string) => {
      setExpandedCategory(
        expandedCategory === categoryName ? null : categoryName
      );
    };

    return (
      <Styled.Wrapper
        className={classnames('TemplateCategories', className)}
        sx={SX.root}
      >
        <Scrollbar sx={SX.scrollbar}>
          {!items.length && (
            <Box sx={SX.loadingWrapper}>
              <Icon name="loading-twotone-loop" size={24} />

              <Typography variant="body3" color="grey.800" fontWeight={600}>
                Loading...
              </Typography>
            </Box>
          )}
          <Stack gap={1.25} ref={containerRef}>
            {items?.map((item) => (
              <TemplateCategory
                key={item.name}
                category={item}
                categoryWithNoChildren={columns?.categoryWithNoChildren}
                expanded={expandedCategory === item.name}
                onToggle={createToggleOptionSelection(item.name)}
                onSelectAll={selectAllOptions(item.name)}
                onDeselectAll={deselectAllOptions(item.name)}
                onToggleEntryChecked={toggleEntryChecked(item.name)}
                onActiveEntry={handleActiveEntry(item.name)}
                selectedOptionsCount={getSelectedOptionsCount(item.name)}
                selectedOptionsCountInEntry={getSelectedOptionsCountForEntry(
                  item.name
                )}
                checkedValues={checkedValues}
                onAddEntry={() => handleAddEntry(item.name)}
                onRemove={handleRemoveEntry(item.name)}
                onClear={() => handleClear(item.name)}
                onToggleExpanded={() => handleCategoryToggle(item.name)}
              />
            ))}
          </Stack>
        </Scrollbar>
      </Styled.Wrapper>
    );
  }
);

TemplateCategories.displayName = 'TemplateCategories';
